/**
 * imageConvert — client-side image preflight pipeline for the document
 * capture surfaces (DocCaptureSurface, DocumentCaptureModule).
 *
 * Two transforms are applied in order:
 *
 *   1. HEIC/HEIF → JPEG (dynamic import of `heic2any`)
 *      iPhones default to capturing photos as `image/heic` (or `image/heif`
 *      on iPad Live Photos). Azure Document Intelligence does not support
 *      HEIC, so we transcode in the browser before upload. heic2any wraps
 *      libheif-js compiled to wasm. Lazy-loaded the first time HEIC lands
 *      so we don't pay the bundle cost on the critical path.
 *
 *   2. Downsample images that exceed `maxBytes`
 *      iPhone JPEGs are commonly 3–5 MB; flatbed scans can be 10–15 MB at
 *      300 DPI. Azure DI's hard cap is 4 MB. Downsample (canvas re-encode at
 *      2000-px max edge, JPEG quality 85) and progressively reduce quality
 *      until the file fits. PDFs cannot be downsampled at this layer — we
 *      reject and ask the officer to re-scan at a lower DPI.
 *
 * Always returns a File; callers feed the result to FileReader → base64.
 */

const HEIC_MIME_TYPES = new Set(['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence']);
const HEIC_EXTENSIONS = /\.(heic|heif)$/i;
const TIFF_MIME_TYPES = new Set(['image/tiff', 'image/tif']);
const TIFF_EXTENSIONS = /\.(tiff?|tif)$/i;

/** Detect HEIC by MIME type or extension. iOS sometimes ships type='' for HEIC. */
function isHeic(file: File): boolean {
  if (HEIC_MIME_TYPES.has(file.type)) return true;
  if (!file.type && HEIC_EXTENSIONS.test(file.name)) return true;
  return false;
}

/** Detect TIFF by MIME type or extension. */
function isTiff(file: File): boolean {
  if (TIFF_MIME_TYPES.has(file.type)) return true;
  if (!file.type && TIFF_EXTENSIONS.test(file.name)) return true;
  return false;
}

/**
 * Convert a TIFF File to a JPEG File via utif (lazy-loaded). Browsers don't
 * render TIFF natively in <img> or <iframe>, so we rasterize to canvas →
 * encode JPEG. Bill 2026-05-01 rule: if we accept it we preview it.
 */
async function tiffToJpeg(file: File): Promise<File> {
  // Lazy-import — utif is small (~20 KB) but no point loading it on the
  // critical path when the typical capture is a JPEG.
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore — utif lacks types in the registry
  const utif = (await import('utif')).default;
  const buf = await file.arrayBuffer();
  const ifds = utif.decode(buf);
  if (!ifds || ifds.length === 0) throw new Error('TIFF decode produced no pages');
  const page = ifds[0];
  utif.decodeImage(buf, page);
  const rgba = utif.toRGBA8(page);
  const w = page.width as number;
  const h = page.height as number;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  const imageData = ctx.createImageData(w, h);
  imageData.data.set(rgba);
  ctx.putImageData(imageData, 0, 0);
  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.92);
  });
  if (!blob) throw new Error('Canvas → JPEG encode failed');
  const newName = file.name.replace(TIFF_EXTENSIONS, '.jpg');
  return new File([blob], newName, { type: 'image/jpeg' });
}

/** Convert a HEIC/HEIF File to a JPEG File via heic2any (lazy-loaded). */
async function heicToJpeg(file: File): Promise<File> {
  // Lazy-import so the libheif-js wasm payload only loads when an officer
  // actually drops a HEIC. Saves bundle size for the typical desktop path.
  const heic2any = (await import('heic2any')).default;
  const out = await heic2any({
    blob: file,
    toType: 'image/jpeg',
    quality: 0.92,
  });
  // heic2any returns Blob | Blob[] depending on whether the source had
  // multiple images. We take the first frame.
  const blob = Array.isArray(out) ? out[0] : out;
  if (!blob) throw new Error('HEIC conversion produced no output');
  const newName = file.name.replace(HEIC_EXTENSIONS, '.jpg');
  return new File([blob], newName, { type: 'image/jpeg' });
}

/**
 * Downsample an image File until it fits under `maxBytes`. Re-encodes via
 * canvas at progressively lower quality / dimensions. Throws if it can't
 * get under the cap after a few passes (very rare for typical photos).
 */
async function downsampleImage(file: File, maxBytes: number): Promise<File> {
  if (file.size <= maxBytes) return file;
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await loadImage(objectUrl);
    // Try a sequence of (maxEdge, quality) pairs until one fits.
    const passes: Array<{ maxEdge: number; quality: number }> = [
      { maxEdge: 2400, quality: 0.88 },
      { maxEdge: 2000, quality: 0.85 },
      { maxEdge: 1600, quality: 0.80 },
      { maxEdge: 1280, quality: 0.75 },
      { maxEdge: 1024, quality: 0.70 },
    ];
    for (const pass of passes) {
      const blob = await encodeAtSize(img, pass.maxEdge, pass.quality);
      if (blob && blob.size <= maxBytes) {
        const newName = stripExtension(file.name) + '.jpg';
        return new File([blob], newName, { type: 'image/jpeg' });
      }
    }
    throw new Error(
      `Image still exceeds ${(maxBytes / 1024 / 1024).toFixed(0)} MB after maximum downsample. Try a lower-resolution scan.`,
    );
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = (): void => resolve(img);
    img.onerror = (): void => reject(new Error('Failed to decode image'));
    img.src = src;
  });
}

function encodeAtSize(
  img: HTMLImageElement,
  maxEdge: number,
  quality: number,
): Promise<Blob | null> {
  const w0 = img.naturalWidth || img.width;
  const h0 = img.naturalHeight || img.height;
  const scale = Math.min(1, maxEdge / Math.max(w0, h0));
  const w = Math.max(1, Math.round(w0 * scale));
  const h = Math.max(1, Math.round(h0 * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return Promise.resolve(null);
  ctx.drawImage(img, 0, 0, w, h);
  return new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), 'image/jpeg', quality);
  });
}

function stripExtension(filename: string): string {
  const dotIdx = filename.lastIndexOf('.');
  return dotIdx > 0 ? filename.substring(0, dotIdx) : filename;
}

/**
 * Rasterize an oversize PDF's FIRST page to a JPEG File (Bill 2026-07-13
 * Item 14 — "accept larger sizes and automatically resize"). ID documents
 * are single-page scans, so page 1 is the document; the render target is
 * ~2400 px on the long edge (≈300 DPI for ID-card geometry), then the normal
 * downsample ladder squeezes it under the Azure DI cap. pdfjs-dist is
 * already a dependency (bundled locally, lazy-imported off the critical path).
 */
async function pdfFirstPageToJpeg(file: File): Promise<File> {
  const pdfjs = await import('pdfjs-dist');
  // Vite worker URL — bundled asset, no CDN (PCB air-gap rule).
  const worker = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
  pdfjs.GlobalWorkerOptions.workerSrc = (worker as { default: string }).default;
  const data = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data }).promise;
  try {
    const page = await doc.getPage(1);
    const base = page.getViewport({ scale: 1 });
    const scale = 2400 / Math.max(base.width, base.height);
    const viewport = page.getViewport({ scale: Math.max(1, scale) });
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(viewport.width);
    canvas.height = Math.round(viewport.height);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context unavailable');
    await page.render({ canvas, canvasContext: ctx, viewport }).promise;
    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.9);
    });
    if (!blob) throw new Error('PDF page → JPEG encode failed');
    return new File([blob], stripExtension(file.name) + '.jpg', { type: 'image/jpeg' });
  } finally {
    await doc.destroy();
  }
}

/**
 * EXIF Orientation (tag 0x0112) of a JPEG, 1-8; 1 when absent or unreadable.
 * Bill 2026-10-16 (sideways scans): a phone photo can carry its rotation only as
 * this flag. The browser shows it upright, but the raw bytes go to Azure DI and
 * into the stored PDF, so the flag must be applied to the pixels first.
 */
export function readJpegExifOrientation(bytes: Uint8Array): number {
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) return 1;
  let i = 2;
  while (i + 4 <= bytes.length) {
    if (bytes[i] !== 0xff) return 1;
    const marker = bytes[i + 1];
    const len = (bytes[i + 2] << 8) | bytes[i + 3];
    if (marker === 0xda || len < 2) return 1; // start of scan — no EXIF before the image data
    if (marker === 0xe1 && i + 10 <= bytes.length
        && bytes[i + 4] === 0x45 && bytes[i + 5] === 0x78 && bytes[i + 6] === 0x69 && bytes[i + 7] === 0x66) {
      const tiff = i + 10;
      if (tiff + 8 > bytes.length) return 1;
      const little = bytes[tiff] === 0x49 && bytes[tiff + 1] === 0x49;
      const u16 = (o: number): number => (little ? bytes[o] | (bytes[o + 1] << 8) : (bytes[o] << 8) | bytes[o + 1]);
      const u32 = (o: number): number =>
        little
          ? (bytes[o] | (bytes[o + 1] << 8) | (bytes[o + 2] << 16) | (bytes[o + 3] << 24)) >>> 0
          : ((bytes[o] << 24) | (bytes[o + 1] << 16) | (bytes[o + 2] << 8) | bytes[o + 3]) >>> 0;
      const ifd = tiff + u32(tiff + 4);
      if (ifd + 2 > bytes.length) return 1;
      const count = u16(ifd);
      for (let e = 0; e < count; e++) {
        const entry = ifd + 2 + e * 12;
        if (entry + 12 > bytes.length) return 1;
        if (u16(entry) === 0x0112) {
          const v = u16(entry + 8);
          return v >= 1 && v <= 8 ? v : 1;
        }
      }
      return 1;
    }
    i += 2 + len;
  }
  return 1;
}

/**
 * Re-encode a JPEG whose EXIF Orientation is not 1 so the pixels are upright and
 * the flag is gone (createImageBitmap applies the flag). Returns the file
 * unchanged when it is already upright or the browser can't decode it.
 */
async function normalizeExifOrientation(file: File): Promise<File> {
  if (file.type !== 'image/jpeg') return file;
  const head = new Uint8Array(await file.slice(0, 128 * 1024).arrayBuffer());
  if (readJpegExifOrientation(head) === 1) return file;
  try {
    const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const canvas = document.createElement('canvas');
    canvas.width = bmp.width;
    canvas.height = bmp.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bmp, 0, 0);
    bmp.close();
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.92));
    return blob ? new File([blob], file.name, { type: 'image/jpeg' }) : file;
  } catch {
    return file;
  }
}

/**
 * Rotate a base64 image clockwise by a quarter turn (90/180/270) and return it
 * as JPEG base64 (no data: prefix). Bill 2026-10-16 — sideways ID scans: the
 * angle comes from Azure DI (server ScanOrientation).
 */
export async function rotateImageBase64(
  base64: string,
  mimeType: string,
  clockwiseDeg: 90 | 180 | 270,
): Promise<{ base64: string; mimeType: string }> {
  const img = await loadImage(`data:${mimeType};base64,${base64}`);
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  const quarter = clockwiseDeg !== 180;
  const canvas = document.createElement('canvas');
  canvas.width = quarter ? h : w;
  canvas.height = quarter ? w : h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((clockwiseDeg * Math.PI) / 180);
  ctx.drawImage(img, -w / 2, -h / 2);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.92));
  if (!blob) throw new Error('Canvas → JPEG encode failed');
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = (): void => resolve(String(fr.result));
    fr.onerror = (): void => reject(new Error('Failed to read rotated image'));
    fr.readAsDataURL(blob);
  });
  return { base64: dataUrl.slice(dataUrl.indexOf(',') + 1), mimeType: 'image/jpeg' };
}

/**
 * Public entry point. Runs HEIC conversion + downsample as needed.
 * Returns the prepared File ready for FileReader → base64.
 *
 * Errors thrown here are user-facing — caller should display them inline
 * on the upload pane.
 */
export async function preflightImageFile(
  file: File,
  maxBytes: number,
): Promise<File> {
  let prepared = file;
  if (isHeic(prepared)) {
    prepared = await heicToJpeg(prepared);
  } else if (isTiff(prepared)) {
    prepared = await tiffToJpeg(prepared);
  } else if (prepared.type === 'application/pdf' && prepared.size > maxBytes) {
    // Oversize PDF → rasterize page 1 and fall through to the downsample
    // ladder instead of rejecting with "re-scan at a lower DPI".
    prepared = await pdfFirstPageToJpeg(prepared);
  }
  // EXIF orientation (Bill 2026-10-16): apply the flag to the pixels so Azure DI,
  // the stored PDF and the preview all see the same upright image.
  prepared = await normalizeExifOrientation(prepared);
  if (prepared.type.startsWith('image/') && prepared.size > maxBytes) {
    prepared = await downsampleImage(prepared, maxBytes);
  }
  return prepared;
}

/** Whether the preflight pipeline accepts this file based on type/extension. */
export function isPreflightAccepted(file: File): boolean {
  if (isHeic(file)) return true;
  if (isTiff(file)) return true;
  return [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/tiff',
  ].includes(file.type);
}

/** MIME types the surfaces should advertise on the file input `accept` attr. */
export const PREFLIGHT_ACCEPTED_MIMES: ReadonlyArray<string> = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/tiff',
  'image/heic',
  'image/heif',
];
